import type {
	CreateItemPayload,
	ShoppingListItemsEntry,
	ShoppingListRepository,
} from './types';
import {buildVisualOrderSyncPayload, needsVisualOrderSync} from './visualOrder';
import {
	type Gsoa_ShoppingList_GetAllShoppingLists_response as AllShoppingLists,
	type Gsoa_ShoppingList_GetAllShoppingListsCollection as ShoppingList,
	type Gsoa_ShoppingList_GetItemsCollection as GetItemsCollection,
	GsoaShoppingListService,
} from '~/openapi';

function toApiItemPayload(item: CreateItemPayload) {
	const {snapshot: _snapshot, product: _product, ...requestBody} = item;
	return requestBody;
}

export function createRemoteShoppingListRepository(): ShoppingListRepository {
	let listsCache: AllShoppingLists | null = null;

	function invalidateCache() {
		listsCache = null;
	}

	return {
		invalidateCache,

		async fetchLists(force = false) {
			if (!force && listsCache) {
				return listsCache;
			}
			const response = await GsoaShoppingListService.getShoppingLists({pageSize: 150});
			if (response) {
				listsCache = response;
			}
			return response;
		},

		async fetchItems(listId?: string, houseNumber?: number) {
			const lists = await this.fetchLists();
			if (!lists?.Items) {
				return [];
			}

			if (listId) {
				const products = await GsoaShoppingListService.getShoppingListItems({
					shoppingListId: listId,
					pageSize: 150,
					houseNumber,
				});
				return products ? [{id: Number(listId), products}] : [];
			}

			const entries = await Promise.all(
				lists.Items
					.filter((list) => list.ItemsCount > 0)
					.map(async ({ID: id}) => {
						const products = await GsoaShoppingListService.getShoppingListItems({
							shoppingListId: id.toString(),
							pageSize: 150,
							houseNumber,
						});
						return products ? {id, products} : null;
					}),
			);

			return entries.filter((entry): entry is ShoppingListItemsEntry => entry !== null);
		},

		async createList(name: string) {
			const response = await GsoaShoppingListService.createNewShoppingList({requestBody: {Name: name}});
			if (response?.ID) {
				invalidateCache();
				const newItem = response as ShoppingList;
				newItem.IsActive = false;
				newItem.IsSelected = false;
				newItem.ItemsCount = 0;
			}
			return response as ShoppingList | null;
		},

		async updateList(listId: string, name: string, isSelected?: boolean) {
			const response = await GsoaShoppingListService.updateShoppingList({
				shoppingListId: listId,
				requestBody: {Name: name, IsSelected: isSelected},
			});
			if (response) {
				invalidateCache();
			}
			return response as unknown as ShoppingList | null;
		},

		async deleteList(listId: string) {
			await GsoaShoppingListService.deleteShoppingList({shoppingListId: listId});
			invalidateCache();
			return true;
		},

		async createItem(shoppingListId: string, item: CreateItemPayload) {
			const response = await GsoaShoppingListService.createNewItem({
				shoppingListId,
				requestBody: toApiItemPayload(item),
			});
			if (response?.ID) {
				invalidateCache();
			}
			return response as GetItemsCollection | null;
		},

		async updateItem(shoppingListId: string, itemId: string, item: CreateItemPayload) {
			const response = await GsoaShoppingListService.updateShoppingListItem({
				shoppingListId,
				itemId,
				requestBody: toApiItemPayload(item),
			});
			return response as GetItemsCollection | null;
		},

		async deleteItem(shoppingListId: string, itemId: string) {
			await GsoaShoppingListService.deleteShoppingListItem({
				shoppingListId,
				itemId,
			});
			invalidateCache();
			return true;
		},

		async clearItems(shoppingListId: string) {
			await GsoaShoppingListService.deleteShoppingListItems({shoppingListId});
			invalidateCache();
			return true;
		},

		async reorderItems(
			shoppingListId: string,
			orderedItemIds: number[],
			sourceItems?: GetItemsCollection[],
			houseNumber?: number,
		) {
			let items = sourceItems;
			if (!items?.length) {
				const response = await GsoaShoppingListService.getShoppingListItems({
					shoppingListId,
					pageSize: 150,
					houseNumber,
				});
				items = response?.Items ?? [];
			}

			if (!needsVisualOrderSync(items, orderedItemIds)) {
				return;
			}

			await GsoaShoppingListService.updateShoppingListItems({
				shoppingListId,
				requestBody: {ItemsUpdate: buildVisualOrderSyncPayload(items, orderedItemIds)},
			});

			invalidateCache();
		},
	};
}
